import { useIsMobile } from "@/hooks/use-mobile";
import { formatAmount, getRequiredFields, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetClearingDocList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { SEARCH_CRITERIA, SEARCH_TYPE, searchType } from "./consts";
import { useNavigate } from "react-router-dom";

const useListing = () => {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const isMobile = useIsMobile();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetClearingDocList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  searchType?.find(
                    (data: any) => data.value == form.watch()[SEARCH_TYPE]
                  )?.operator || "CONTAINS",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc(), 100);
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.clearing}`,
        name: "Clearing",
      },
      {
        link: "",
        name: <span className="text-primary">Clearing Documents</span>,
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.clearing}`);
  };

  // Define Table Columns
  const columns = [
    {
      accessorKey: "postDocumentHeaderId",
      header: <div>Posted Doc.ID</div>,
      accessorFn: (row: any) => String(row?.postDocumentHeaderId || ""),
    },
    {
      accessorKey: "noOfDocuments",
      header: <div># Document</div>,
      accessorFn: (row: any) => String(row?.noOfDocuments || ""),
    },
    {
      accessorKey: "postingDate",
      header: <div>Posting Date</div>,
      accessorFn: (row: any) =>
        row?.postingDate
          ? moment(row?.postingDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
      accessorFn: (row: any) => String(row?.propertyName || ""),
    },
    {
      accessorKey: "ownerName",
      header: <div>Business Partner</div>,
      accessorFn: (row: any) => String(row?.ownerName || ""),
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Billed Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.amount || "0"),
    },
    {
      accessorKey: "clearingAmount",
      meta: { align: "right" },
      header: <div>Cleared Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.clearingAmount || "0"),
    },
    {
      accessorKey: "variance",
      header: <div>Variance Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.variance || "0"),
    },
    {
      accessorKey: "createdByName",
      header: <div>Cleared By</div>,
      accessorFn: (row: any) => row?.createdByName || "",
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.postDocumentHeaderId}/view`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onBackClick,
    currentFinanceYear
  };
};

export default useListing;
